/**
 * AccountList Component
 * Displays accounts grouped by type
 */
import { cn } from '@/utils/cn';
import { formatCurrency } from '@/utils/format';
import AccountCard, { AccountCardSkeleton } from './AccountCard';
import { getAccountTypeName } from './AccountTypeIcon';
import type { AccountWithConnection } from '@/services/accounts';
import type { AccountType } from '@/types';

export interface AccountListProps {
  groupedAccounts: [string, AccountWithConnection[]][];
  showTotals?: boolean;
  className?: string;
  /** Callback when close button is clicked on a manual account */
  onCloseClick?: (account: AccountWithConnection) => void;
  /** Callback when edit button is clicked on any account - WP-FLOW-7 */
  onEditClick?: (account: AccountWithConnection) => void;
  /** Callback when balance history button is clicked on any account - WP-FLOW-10 */
  onBalanceHistoryClick?: (account: AccountWithConnection) => void;
  /** Callback when holdings button is clicked on any account - WP-MANUAL-HOLD-2 */
  onHoldingsClick?: (account: AccountWithConnection) => void;
  /** Callback when merge button is clicked on any account */
  onMergeClick?: (account: AccountWithConnection) => void;
  /** Callback when delete button is clicked on an orphaned account - WP-ACCT-2 */
  onDeleteClick?: (account: AccountWithConnection) => void;
  /** Callback when hide/unhide button is clicked on any account - WP-BUG-62 */
  onToggleVisibilityClick?: (account: AccountWithConnection) => void;
}

export default function AccountList({
  groupedAccounts,
  showTotals = true,
  className,
  onCloseClick,
  onEditClick,
  onBalanceHistoryClick,
  onHoldingsClick,
  onMergeClick,
  onDeleteClick,
  onToggleVisibilityClick,
}: AccountListProps) {
  if (groupedAccounts.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <NoAccountsIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">No accounts found</h3>
        <p className="mt-1 text-sm text-gray-500">
          Link your bank accounts to get started.
        </p>
      </div>
    );
  }

  return (
    <div className={cn('space-y-6', className)}>
      {groupedAccounts.map(([type, accounts]) => {
        const typeName = getAccountTypeName(type as AccountType);
        const total = accounts.reduce((sum, acc) => {
          // Backend sends balances with correct sign, sum directly (matches ConnectionCard)
          return sum + acc.balance;
        }, 0);

        return (
          <div key={type}>
            {/* Group header */}
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                {typeName}
              </h3>
              {showTotals && (
                <span
                  className={cn(
                    'text-sm font-medium tabular-nums',
                    total < 0 ? 'text-red-600' : 'text-gray-700'
                  )}
                >
                  {formatCurrency(total)}
                </span>
              )}
            </div>

            {/* Account cards */}
            <div className="space-y-2">
              {accounts.map((account) => (
                <AccountCard
                  key={account.id}
                  account={account}
                  onCloseClick={onCloseClick}
                  onEditClick={onEditClick}
                  onBalanceHistoryClick={onBalanceHistoryClick}
                  onHoldingsClick={onHoldingsClick}
                  onMergeClick={onMergeClick}
                  onDeleteClick={onDeleteClick}
                  onToggleVisibilityClick={onToggleVisibilityClick}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Loading skeleton for AccountList
 */
export function AccountListSkeleton({ groups = 3 }: { groups?: number }) {
  return (
    <div className="space-y-6">
      {Array.from({ length: groups }).map((_, groupIndex) => (
        <div key={groupIndex}>
          {/* Group header skeleton */}
          <div className="flex items-center justify-between mb-3">
            <div className="h-4 w-24 bg-gray-200 rounded animate-pulse" />
            <div className="h-4 w-20 bg-gray-200 rounded animate-pulse" />
          </div>

          {/* Account card skeletons */}
          <div className="space-y-2">
            {Array.from({ length: groupIndex === 0 ? 2 : 1 }).map((_, cardIndex) => (
              <AccountCardSkeleton key={cardIndex} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function NoAccountsIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
      />
    </svg>
  );
}
